import { memo, useEffect, useRef, useState } from 'react';
import torusAlpha from '@/assets/brand/torus-alpha.webp';
import {
  KorzaTorus,
  type KorzaTorusControls,
} from '@/components/brand/korza-torus/korza-torus';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { TorusSpot } from '@/lib/whats-new/edition-types';

interface TorusPlacement {
  x: number;
  y: number;
  zoom: number;
  opacity: number;
}

/**
 * Where the torus sits per slide kind (prototype v5, NEWS-10): whole, right
 * of the copy on the cover and the outro (≥ 768px) or in the top half under
 * the header (< 768px); hidden on the content slides.
 */
const TORUS_SPOTS: Readonly<
  Record<TorusSpot, { desktop: TorusPlacement; mobile: TorusPlacement }>
> = {
  cover: {
    desktop: { x: 2.6, y: -0.05, zoom: 0.88, opacity: 1 },
    mobile: { x: 0.45, y: 1.85, zoom: 0.55, opacity: 1 },
  },
  content: {
    desktop: { x: 2.7, y: 0, zoom: 0.74, opacity: 0 },
    mobile: { x: 0, y: 2.1, zoom: 0.6, opacity: 0 },
  },
  outro: {
    desktop: { x: 2.6, y: 0, zoom: 0.9, opacity: 1 },
    mobile: { x: 0.5, y: 2.45, zoom: 0.5, opacity: 1 },
  },
};

/** Opacity fades in 1.2 s (NEWS-10). */
// Arbitrary properties: Tailwind's arbitrary duration and easing classes
// clash with tailwindcss-animate's and generate nothing.
const FADE =
  'transition-opacity [transition-duration:1200ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)]';

/**
 * The still torus where WebGL isn't available (NEWS-11), in the same spot;
 * nothing at all if the image fails too.
 */
function TorusStill({ opacity }: { opacity: number }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={torusAlpha}
      alt=""
      aria-hidden="true"
      data-testid="stage-torus-still"
      onError={() => setFailed(true)}
      style={{ opacity }}
      className={cn(
        'pointer-events-none absolute -z-20 select-none',
        'right-[-30px] top-1/2 w-[460px] -translate-y-1/2',
        // < 768px: in the top half, under the header (bars + buttons).
        'max-md:left-1/2 max-md:right-auto max-md:top-[calc(80px+env(safe-area-inset-top))] max-md:w-[min(280px,72%)] max-md:-translate-x-1/2 max-md:translate-y-0',
        FADE
      )}
    />
  );
}

/**
 * The player's one torus (NEWS-10): glides to each slide's spot, fades out
 * on the content slides and gets a `boost()` on every slide change (not
 * when the player opens).
 */
export const StageTorus = memo(function StageTorus({
  spot,
  visit,
}: {
  spot: TorusSpot;
  /** The player's visit counter: changes on every slide change. */
  visit: number;
}) {
  const isMobile = useIsMobile();
  const torusRef = useRef<KorzaTorusControls>(null);
  const openedOnVisit = useRef(visit);
  const placement = TORUS_SPOTS[spot][isMobile ? 'mobile' : 'desktop'];

  useEffect(() => {
    torusRef.current?.moveTo(placement.x, placement.y, placement.zoom);
  }, [placement]);

  useEffect(() => {
    if (visit !== openedOnVisit.current) torusRef.current?.boost();
  }, [visit]);

  return (
    <KorzaTorus
      ref={torusRef}
      opacity={placement.opacity}
      className={cn(
        'pointer-events-none absolute inset-0 -z-20 size-full',
        FADE
      )}
      fallback={<TorusStill opacity={placement.opacity} />}
    />
  );
});
